
import { defineComponent, onMounted, computed, PropType, ref, nextTick, watch } from 'vue'
import ListHeader from '@/components/ListHeader.vue'
import { keys } from 'lodash'

export interface ListOption {
  label: string,
  value: any,
  data?: any,
  subtitle?: ((option: ListOption, options: Array<ListOption>) => string) | string
}

export default defineComponent({
  name: 'SelectHeader',
  components: { ListHeader },
  props: {
    modelValue: {
      type: [String, Object], // as PropType<ListOption>
      default: 'Select an option...'
    },
    options: {
      type: Array as PropType<Array<ListOption | string>>
    },
    optionsMap: {
      type: Object as PropType<ListOption>, // defines fields mapping per list-option key
      default: () => ({})
    },
    selection: {
      type: Object as PropType<ListOption>
    },
    loading: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Click to select'
    },
    createLabel: {
      type: String,
      default: '+ Create new'
    },
    prepend: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: '...'
    },
    onCreate: {
      type: Function
    },
    onUpdate: {
      type: Function
    }
  },
  emits: ['update:selection', 'update:modelValue', 'update:options', 'update:loading', 'create'],
  setup (props, { emit }) {
    const showSelect = ref<boolean>(false)
    const showCreate = ref<boolean>(false)
    const createName = ref('')
    const createinput = ref()
    const parsedOptions = computed(() => {
      let options = (props.options || [])

      if (props.optionsMap && Object.keys(props.optionsMap).length) {
        options = options.map(o => {
          const parsedOption = typeof o === 'string'
            ? { label: o, value: o }
            : o

          // use the provided optionsMap to create a ListOption from the provided option object
          return {
            ...parsedOption,
            ...(Object.fromEntries(
              Object.keys(props.optionsMap).map(key => {
                return [key, typeof props.optionsMap[key] === 'boolean' ? o : typeof props.optionsMap[key] === 'function' ? props.optionsMap[key](parsedOption, key) : parsedOption[props.optionsMap[key]]]
              }))
            )
          }
        }) as Array<ListOption>
      }

      return options.map(o => {
        return typeof o === 'string'
          ? { label: o, value: o }
          : o
      })
    })

    const filteredOptions = computed(() => {
      return (parsedOptions.value || []).filter(f => f.label && f.label !== activeOption.value?.label)
    })

    const activeOption = ref<ListOption>(parsedOptions.value[0])

    function setActive (option: ListOption) {
      let emitValue = option.value
      if (props.onUpdate && typeof props.onUpdate === 'function') {
        emitValue = props.onUpdate(option.value)
      }
      activeOption.value = option
      showSelect.value = false
      emit('update:modelValue', emitValue)
      emit('update:selection', option)
    }

    function getSubtitle (option: ListOption): string {
      const o = parsedOptions.value.find(f => f.label === option.label)
      return o?.subtitle
        ? typeof o.subtitle === 'function'
          ? o.subtitle(o, parsedOptions.value)
          : o.subtitle
        : ''
    }

    const subtitles = computed(() => {
      return filteredOptions.value.map(o => getSubtitle(o))
    })

    const subtitle = computed(() => {
      return activeOption.value ? getSubtitle(activeOption.value) : props.description
    })

    async function create (event: KeyboardEvent) {
      showCreate.value = false
      emit('update:loading', true)

      try {
        if (typeof props.onCreate === 'function') {
          const created = await props.onCreate(createName.value)

          emit('create', createName.value)
          emit('update:loading', false)
          // console.log('created', created)

          if (created) {
            emit('update:options', [...(props.options || []), created])
            setActive(created)
          }
          else showSelect.value = false
        } else {
          throw Error('props.onCreate was not a function')
        }
      } catch (e) {
        console.error('listHeader.create', e)
        emit('update:loading', false)
      }
    }

    onMounted(() => {
      if (parsedOptions.value?.length) {
        const opt = parsedOptions.value?.find(option => typeof props.modelValue === 'string' ? option.value === props.modelValue : option.value === props.modelValue?.value)
        setActive(opt || parsedOptions.value[0])
      }
    })

    watch(() => props.modelValue, (value, next) => {
      const opt = parsedOptions.value?.find(option =>
        typeof value === 'string'
          ? option.value === value
          : value?.sys
            ? option.value?.sys?.id === value.sys.id
            : option.value === value?.value)

      if (opt) setActive(opt)
    })

    return {
      beforeCreate: e => {
        showCreate.value = true
        void nextTick(() => {
          createinput.value?.$el?.childNodes[0].focus()
        })
      },
      parsedOptions,
      filteredOptions,
      activeOption,
      showSelect,
      setActive,
      subtitle,
      subtitles,
      getSubtitle,
      create,
      showCreate,
      createName,
      createinput
    }
  }
})
